<div class="notes-panel">

  <!-- ── Toolbar: prominent search + filters + primary action ── -->
  <div class="np-toolbar">
    <div class="np-search-box">
      <mat-icon class="np-search-icon">search</mat-icon>
      <input type="text"
        class="np-search-input"
        placeholder="Search notes by text or author…"
        matTooltip="Filter the notes below by their content or who wrote them"
        matTooltipPosition="below"
        [(ngModel)]="searchQuery" />
      @if (searchQuery) {
        <button class="np-search-clear" matTooltip="Clear search" (click)="searchQuery = ''">
          <mat-icon>close</mat-icon>
        </button>
      }
    </div>

    @if (pinnedCount) {
      <button class="np-filter-chip"
        [class.active]="pinnedOnly"
        [matTooltip]="pinnedOnly ? 'Show all notes' : 'Show only pinned notes'"
        (click)="togglePinnedOnly()">
        <mat-icon>push_pin</mat-icon>
        <span class="np-chip-label">Pinned</span>
        <span class="np-chip-count">{{ pinnedCount }}</span>
      </button>
    }

    @if (!isAdding) {
      <button class="np-add-btn"
        [disabled]="isLocked"
        [matTooltip]="isLocked ? lockTooltip : 'Add a note to this ' + recordNoun"
        matTooltipPosition="below"
        (click)="isAdding = true">
        <mat-icon>add</mat-icon> <span class="np-add-label">Add Note</span>
      </button>
    }
  </div>

  <!-- ── Add Note form ── -->
  @if (isAdding) {
    <div class="np-add-form">
      <textarea [(ngModel)]="newNoteText"
        [placeholder]="'Add a note about this ' + recordNoun + '…'"
        class="np-textarea"
        rows="3"></textarea>
      <div class="np-form-footer">
        <button class="np-pin-toggle"
          [class.active]="newNotePinned"
          [matTooltip]="newNotePinned ? 'This note will be pinned to the top' : 'Pin this note to the top of the list'"
          (click)="newNotePinned = !newNotePinned">
          <mat-icon>push_pin</mat-icon>
          {{ newNotePinned ? 'Pinned' : 'Pin Note' }}
        </button>
        <div class="np-form-actions">
          <button class="np-cancel-btn" matTooltip="Discard this note" (click)="cancelAdd()">Cancel</button>
          <button class="np-save-btn"
            [disabled]="!newNoteText.trim()"
            [matTooltip]="'Save this note to the ' + recordNoun"
            (click)="handleAddNote()">Save Note</button>
        </div>
      </div>
    </div>
  }

  <!-- ── Notes list ── -->
  <div class="np-notes-list">
    @if (isLoading) {
      <div class="np-state">
        <mat-icon class="np-state-icon spin">progress_activity</mat-icon>
        <span>Loading notes…</span>
      </div>
    } @else if (error) {
      <div class="np-state np-state-error">
        <mat-icon class="np-state-icon">error_outline</mat-icon>
        <span>{{ error }}</span>
      </div>
    } @else if (filteredNotes.length === 0) {
      <div class="np-state">
        <mat-icon class="np-state-icon">{{ searchQuery || pinnedOnly ? 'search_off' : 'sticky_note_2' }}</mat-icon>
        @if (searchQuery || pinnedOnly) {
          <span>No notes match your filters.</span>
          <button class="np-state-action" (click)="clearFilters()">Clear filters</button>
        } @else {
          <span>No notes on this {{ recordNoun }} yet.</span>
          @if (!isLocked) {
            <button class="np-state-action" (click)="isAdding = true">Write the first one</button>
          }
        }
      </div>
    }

    @for (note of filteredNotes; track trackByNoteId($index, note)) {
      <div class="np-note-card" [class.pinned]="note.isPinned">
        <div class="np-note-header">
          <div class="np-author">
            <div class="np-author-avatar" [matTooltip]="'Written by ' + note.author">{{ note.authorInitials }}</div>
            <div class="np-author-meta">
              <span class="np-author-name">{{ note.author }}</span>
              @if (note.timestamp) {
                <span class="np-timestamp"
                  [matTooltip]="'Created ' + (note.timestamp | date:'MMM d, y, h:mm a')">
                  {{ note.timestamp | date:'MMM d, y' }}
                </span>
              }
            </div>
            @if (note.isPinned) {
              <mat-icon class="np-pin-icon" matTooltip="Pinned note">push_pin</mat-icon>
            }
          </div>

          <div class="np-note-actions">
            <button class="np-note-btn" matTooltip="Open the full note" (click)="handleView(note)">
              <mat-icon>visibility</mat-icon>
            </button>
            <button class="np-note-btn"
              [disabled]="isLocked"
              [matTooltip]="isLocked ? lockTooltip : 'Edit this note'"
              (click)="handleEdit(note)">
              <mat-icon>edit</mat-icon>
            </button>
            <button class="np-note-btn" matTooltip="More actions" [matMenuTriggerFor]="noteMenu">
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #noteMenu="matMenu">
              <button mat-menu-item (click)="handleExport(note)">
                <mat-icon>picture_as_pdf</mat-icon><span>Export as PDF</span>
              </button>
              <button mat-menu-item class="np-menu-delete" [disabled]="isLocked" (click)="handleDelete(note.id)">
                <mat-icon>delete</mat-icon><span>Delete</span>
              </button>
            </mat-menu>
          </div>
        </div>

        <p class="np-note-text">{{ note.text }}</p>
      </div>
    }
  </div>

  <!-- ── Footer ── -->
  @if (!isLoading && notes.length) {
    <div class="np-footer">
      <span class="np-note-count">
        {{ filteredNotes.length }}
        @if (searchQuery || pinnedOnly) { of {{ notes.length }} }
        {{ notes.length === 1 && !searchQuery && !pinnedOnly ? 'note' : 'notes' }}
      </span>
      @if (isLocked) {
        <span class="np-locked-note" [matTooltip]="lockTooltip">
          <mat-icon>lock</mat-icon> Read-only
        </span>
      }
    </div>
  }
</div>
